/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { useRef, useState, useEffect } from "react";
import { BaseFieldProps, formatCurrency, parseCurrencyToNumber } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

type FieldCurrencyProps = BaseFieldProps & {
    /** Para birimi simgesi (varsayılan: ₺) */
    currencySymbol?: string;
    /** Para birimi simgesinin konumu */
    symbolPosition?: "left" | "right";
    /** Ondalık basamak sayısı (varsayılan: 2) */
    decimals?: number;
    /** Minimum değer */
    min?: number;
    /** Maksimum değer */
    max?: number;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
};

export function FieldCurrency({
    name,
    label,
    description,
    className,
    labelType = "text",
    tooltipContent,
    currencySymbol = "₺",
    symbolPosition = "right",
    decimals = 2,
    min,
    max,
    placeholder = "0,00",
    disabled,
    readOnly,
}: FieldCurrencyProps) {
    const [displayValue, setDisplayValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <FormField
            name={name}
            render={({ field }) => {
                // Sync display value with field value when not focused
                useEffect(() => {
                    if (!isFocused) {
                        const formatted = formatCurrency(field.value, decimals);
                        setDisplayValue(formatted);
                    }
                }, [field.value, isFocused, decimals]);

                const handleFocus = () => {
                    setIsFocused(true);
                    // Focus olduğunda sayıyı olduğu gibi göster (formatlanmış halde)
                };

                const handleBlur = () => {
                    setIsFocused(false);
                    field.onBlur();

                    // Parse ve validate
                    let numValue = parseCurrencyToNumber(displayValue);

                    // Min/max kontrol
                    if (min !== undefined && numValue < min) numValue = min;
                    if (max !== undefined && numValue > max) numValue = max;

                    // Field value güncelle (string olarak)
                    field.onChange(numValue === 0 && displayValue === "" ? "" : numValue.toString());

                    // Display value güncelle (formatlı)
                    setDisplayValue(formatCurrency(numValue, decimals));
                };

                const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const input = e.target;
                    let value = input.value;
                    const cursorPos = input.selectionStart || 0;

                    // Sadece sayı, nokta ve virgül kabul et
                    value = value.replace(/[^0-9.,]/g, "");

                    // Birden fazla virgül engelle
                    const commaCount = (value.match(/,/g) || []).length;
                    if (commaCount > 1) {
                        value = value.replace(/,/g, (match, index) => {
                            return value.indexOf(",") === index ? match : "";
                        });
                    }

                    // Virgülden sonra maksimum decimal basamak
                    const commaIndex = value.indexOf(",");
                    if (commaIndex !== -1 && value.length - commaIndex - 1 > decimals) {
                        value = value.substring(0, commaIndex + decimals + 1);
                    }

                    // Mevcut noktaları kaldır (binlik ayracı)
                    const cleanValue = value.replace(/\./g, "");

                    // Virgülden önceki ve sonraki kısımları ayır
                    const parts = cleanValue.split(",");
                    const integerPart = parts[0] || "";
                    const decimalPart = parts[1];

                    // Binlik ayracı ekle (sadece integer kısmına)
                    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

                    // Formatlanmış değeri oluştur
                    const formattedValue = decimalPart !== undefined
                        ? `${formattedInteger},${decimalPart}`
                        : formattedInteger;

                    // Cursor pozisyonunu hesapla (eklenen noktaları dikkate al)
                    const oldDotCount = (displayValue.substring(0, cursorPos).match(/\./g) || []).length;
                    const newDotCount = (formattedValue.substring(0, cursorPos + (formattedValue.length - value.length)).match(/\./g) || []).length;
                    const dotDiff = newDotCount - oldDotCount;

                    setDisplayValue(formattedValue);

                    // Cursor pozisyonunu güncelle
                    setTimeout(() => {
                        const newCursorPos = Math.max(0, cursorPos + dotDiff);
                        input.setSelectionRange(newCursorPos, newCursorPos);
                    }, 0);

                    // Gerçek zamanlı olarak field value güncelle (string olarak)
                    const numValue = parseCurrencyToNumber(formattedValue);
                    field.onChange(formattedValue === "" ? "" : numValue.toString());
                };

                const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                    // Nokta tuşunu virgüle çevir (numpad ve normal klavye desteği)
                    if (e.key === ".") {
                        e.preventDefault();
                        // Zaten virgül varsa ekleme
                        if (displayValue.includes(",")) return;
                        const input = inputRef.current;
                        if (!input) return;
                        const start = input.selectionStart || 0;
                        const end = input.selectionEnd || 0;
                        // Virgülü cursor pozisyonuna ekle
                        const newValue = displayValue.substring(0, start) + "," + displayValue.substring(end);
                        setDisplayValue(newValue);
                        // Field value güncelle
                        const numValue = parseCurrencyToNumber(newValue);
                        field.onChange(newValue === "" ? "" : numValue.toString());
                        // Cursor pozisyonunu virgülden sonraya ayarla
                        setTimeout(() => {
                            input.setSelectionRange(start + 1, start + 1);
                        }, 0);
                        return;
                    }

                    // Sadece sayısal tuşlar, backspace, delete, tab, arrow keys, virgül
                    const allowedKeys = [
                        "Backspace", "Delete", "Tab", "Escape", "Enter",
                        "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
                        "Home", "End", ","
                    ];

                    if (allowedKeys.includes(e.key)) return;
                    if (e.key >= "0" && e.key <= "9") return;
                    if (e.ctrlKey || e.metaKey) return; // Copy/paste izin ver

                    e.preventDefault();
                };

                return (
                    <FormItem className={className}>
                        <FormFieldLabel label={label} labelType={labelType} tooltipContent={tooltipContent} />
                        <FormControl>
                            <div className="relative">
                                {symbolPosition === "left" && currencySymbol && (
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                                        {currencySymbol}
                                    </span>
                                )}
                                <Input
                                    ref={(e) => {
                                        field.ref(e);
                                        (inputRef as any).current = e;
                                    }}
                                    type="text"
                                    inputMode="decimal"
                                    value={displayValue}
                                    onChange={handleChange}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    className={`text-right ${symbolPosition === "left" ? "pl-8" : ""} ${symbolPosition === "right" ? "pr-8" : ""}`}
                                />
                                {symbolPosition === "right" && currencySymbol && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                                        {currencySymbol}
                                    </span>
                                )}
                            </div>
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
